'use client';

import { FC, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ArrowRight, Info } from 'lucide-react';
import { workingDaysList } from '@/hotdesk-ui/constants/WFO.constant';
import HotdeskButton from '@/hotdesk-ui/components/shared/HotdeskButton';
import { useCalculatorContext } from '../CalculatorContext';
import calcStyles from '../Calculator.module.scss';
import styles from './Information.module.scss';
import { loadGoogleMaps } from '@/hotdesk-ui/helpers/google-maps-loader';
interface InformationProps {
  countryList: {
    id: number;
    name_en: string;
    currency: string;
  }[];
  curencyList: {
    value: string;
    label: string;
  }[];
  onSubmit: (data: any) => void;
  formData: {
    [key: string]: any;
  };
  setCountry: (country: string | undefined) => void;
}
interface FormSchema {
  country: string;
  employees: number;
  currency: string;
  workingDays: string;
  budget: number;
}
export const Information: FC<InformationProps> = ({
  onSubmit,
  countryList,
  formData,
  setCountry
}) => {
  const t = useTranslations();
  const [currencyList, setCurrencyList] = useState([{
    value: 'USD',
    label: 'USD'
  }]);
  const [currency, setCurrency] = useState('');
  const {
    setCountryCode
  } = useCalculatorContext();
  const schema = yup.object().shape({
    country: yup.string().required(t('calculator.Business-information.form.country') + ' ' + t('calculator.Business-information.form.required')),
    employees: yup.number().typeError(t('calculator.Business-information.form.employees') + ' ' + t('calculator.Business-information.form.required')).required(t('calculator.Business-information.form.employees') + ' ' + t('calculator.Business-information.form.required')).min(1, t('calculator.Business-information.form.valid-num')).max(999999, t('calculator.Business-information.form.valid-num')),
    currency: yup.string().required(t('calculator.Business-information.form.curency') + ' ' + t('calculator.Business-information.form.required')),
    workingDays: yup.string().required(t('calculator.Business-information.form.WFO.title') + ' ' + t('calculator.Business-information.form.required')),
    budget: yup.number().typeError(t('calculator.Business-information.form.budget') + ' ' + t('calculator.Business-information.form.required')).required(t('calculator.Business-information.form.budget') + ' ' + t('calculator.Business-information.form.required')).min(1, t('calculator.Business-information.form.valid-num')).test('len', t('calculator.Business-information.form.valid-num'), val => val != null && val.toString().length < 21)
  });
  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    formState,
    control
  } = useForm<FormSchema>({
    defaultValues: formData as any,
    resolver: yupResolver(schema)
  });
  const {
    errors
  } = formState;
  const onError = async () => {
    await trigger();
  };
  function getCurrency(currencyCode: string | undefined) {
    if (currencyCode) {
      setCurrencyList([{
        value: currencyCode,
        label: currencyCode
      }, {
        value: 'USD',
        label: 'USD'
      }]);
      setCurrency(currencyCode);
    } else {
      setValue('currency', '');
      setCurrencyList([{
        value: 'USD',
        label: 'USD'
      }]);
    }
  }
  useEffect(() => {
    if (currency) setValue('currency', currency);
  }, [currency, setValue]);
  useEffect(() => {
    handleGetPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleGetPermission = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(({
        coords
      }) => {
        const {
          latitude,
          longitude
        } = coords;
        loadGoogleMaps().then(() => {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({
            location: {
              lat: latitude,
              lng: longitude
            }
          }, (results: any, status: string) => {
            if (status === 'OK' && results[0]) {
              const formattedAddress = results[9]?.formatted_address;
              const selectedCountryObj = countryList.find(country => country.name_en.toString() === formattedAddress);
              setCountryCode(results[1]?.address_components[5]?.short_name);
              const country = getValues('country');
              if (selectedCountryObj && country == null) {
                setValue('country', selectedCountryObj.id.toString());
                setValue('currency', selectedCountryObj.currency);
              }
            }
          });
        });
      });
    }
  };
  const renderTooltip = (text: string) => (props: any) => <Tooltip id="wfo-tooltip" {...props}>
            {text}
        </Tooltip>;
  return <div className={calcStyles.layout} data-sentry-component="Information" data-sentry-source-file="Information.tsx">
            <div className={calcStyles.intro}>
                <h1 className={calcStyles.introTitle}>
                    {t('calculator.Business-information.title')}
                </h1>
                <p className={calcStyles.introDesc}>
                    {t('calculator.Business-information.desc')}
                </p>
                <span className={calcStyles.dotsProgress} aria-hidden="true">
                    <span className={`${calcStyles.dot} ${calcStyles.dotActive}`} />
                    <span className={calcStyles.dot} />
                    <span className={calcStyles.dot} />
                </span>
            </div>

            <div className={calcStyles.card}>
                <form className={styles.form} onSubmit={handleSubmit(onSubmit, onError)}>
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="country">
                            {t('calculator.Business-information.form.country')}
                            <span className={styles.required}>*</span>
                        </label>
                        <Controller control={control} name="country" render={({
            field: {
              onChange,
              value
            }
          }) => <div className={`${styles.selectWrap} ${errors.country ? styles.selectInvalid : ''}`}>
                                    <Select instanceId="countryList" options={countryList} isSearchable placeholder={t('calculator.Business-information.form.ph')} classNamePrefix="react-select" value={value ? countryList.find(option => option.id.toString() === value.toString()) || null : null} onChange={selectedOption => {
              onChange(selectedOption ? selectedOption.id : '');
              getCurrency(selectedOption?.currency);
              setCountry(selectedOption?.name_en);
            }} onBlur={() => trigger('country')} getOptionLabel={option => option.name_en} getOptionValue={option => option.id.toString()} />
                                </div>} data-sentry-element="Controller" data-sentry-source-file="Information.tsx" />
                        {errors.country && <p className={styles.error}>
                                {errors.country.message}
                            </p>}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="currency">
                            {t('calculator.Business-information.form.curency')}
                            <span className={styles.required}>*</span>
                        </label>
                        <Controller control={control} name="currency" render={({
            field: {
              onChange,
              value
            }
          }) => <div className={`${styles.selectWrap} ${errors.currency ? styles.selectInvalid : ''}`}>
                                    <Select instanceId="currencyList" options={currencyList} isSearchable={false} placeholder={t('calculator.Business-information.form.ph')} classNamePrefix="react-select" value={value ? {
              value: value,
              label: value
            } : null} onChange={selectedOption => onChange(selectedOption ? selectedOption.value : '')} onBlur={() => trigger('currency')} getOptionLabel={option => option.label} getOptionValue={option => option.value} />
                                </div>} data-sentry-element="Controller" data-sentry-source-file="Information.tsx" />
                        {errors.currency && <p className={styles.error}>
                                {errors.currency.message}
                            </p>}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="employees">
                            {t('calculator.Business-information.form.employees')}
                            <span className={styles.required}>*</span>
                        </label>
                        <input id="employees" type="number" placeholder="e.g. 50" className={`${styles.input} ${styles.inputWithSuffix} ${errors.employees ? styles.inputInvalid : ''}`} {...register('employees')} onBlur={() => trigger('employees')} />
                        <span className={styles.suffix}>
                            {t('calculator.Business-information.form.members')}
                        </span>
                        {errors.employees && <p className={styles.error}>
                                {errors.employees.message}
                            </p>}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="workingDays">
                            {t('calculator.Business-information.form.WFO.title')}
                            <span className={styles.required}>*</span>
                            <OverlayTrigger placement="top" delay={{
              show: 250,
              hide: 400
            }} overlay={renderTooltip(t('calculator.Business-information.form.WFO.desc'))} data-sentry-element="OverlayTrigger" data-sentry-source-file="Information.tsx">
                                <button type="button" className={styles.tooltipBtn} aria-label="Help">
                                    <Info size={14} data-sentry-element="Info" data-sentry-source-file="Information.tsx" />
                                </button>
                            </OverlayTrigger>
                        </label>
                        <Controller control={control} name="workingDays" render={({
            field: {
              onChange,
              value
            }
          }) => <div className={`${styles.selectWrap} ${errors.workingDays ? styles.selectInvalid : ''}`}>
                                    <Select instanceId="workingDaysList" options={workingDaysList} isSearchable={false} placeholder={t('calculator.Business-information.form.ph')} classNamePrefix="react-select" formatOptionLabel={option => <div className={styles.wfoOption}>
                                                <span>{t(option.text)}</span>
                                                <span>{t(option.desc)}</span>
                                            </div>} value={value ? workingDaysList.find(option => option.value === value) || null : null} onChange={selectedOption => onChange(selectedOption ? selectedOption.value : '')} onBlur={() => trigger('workingDays')} getOptionValue={option => option.value} />
                                </div>} data-sentry-element="Controller" data-sentry-source-file="Information.tsx" />
                        {errors.workingDays && <p className={styles.error}>
                                {errors.workingDays.message}
                            </p>}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="budget">
                            {t('calculator.Business-information.form.budget')}
                            <span className={styles.required}>*</span>
                            <OverlayTrigger placement="top" delay={{
              show: 250,
              hide: 400
            }} overlay={renderTooltip(t('calculator.Business-information.form.budget-desc'))} data-sentry-element="OverlayTrigger" data-sentry-source-file="Information.tsx">
                                <button type="button" className={styles.tooltipBtn} aria-label="Help">
                                    <Info size={14} data-sentry-element="Info" data-sentry-source-file="Information.tsx" />
                                </button>
                            </OverlayTrigger>
                        </label>
                        <div className={styles.inputPrefixWrap}>
                            <input id="budget" type="number" placeholder="0.00" className={`${styles.input} ${styles.inputWithPrefix} ${errors.budget ? styles.inputInvalid : ''}`} {...register('budget')} onBlur={() => trigger('budget')} />
                            <span className={styles.prefix}>
                                {getValues('currency') || '$'}
                            </span>
                        </div>
                        {errors.budget && <p className={styles.error}>
                                {errors.budget.message}
                            </p>}
                    </div>

                    <div className={styles.actions}>
                        <HotdeskButton type="submit" variant="primary" size="md" iconRight={<ArrowRight size={18} />} data-sentry-element="HotdeskButton" data-sentry-source-file="Information.tsx">
                            {t('calculator.Business-information.form.button')}
                        </HotdeskButton>
                    </div>
                </form>
            </div>
        </div>;
};
export default Information;